import { useEffect, useState } from 'react';
import { Copy, KeyRound, UserPlus } from 'lucide-react';

import { Modal, Panel, Spinner, relativeTime } from '../../components/common/index.jsx';
import { admin } from '../../lib/api.js';
import { useUiStore } from '../../store/uiStore.js';

export function UsersPage() {
  const [users, setUsers] = useState(null);
  const [creating, setCreating] = useState(false);
  const [credential, setCredential] = useState(null);
  const toast = useUiStore((s) => s.toast);

  const refresh = () => admin.users().then((r) => setUsers(r.items)).catch(() => setUsers([]));
  useEffect(() => {
    refresh();
  }, []);

  if (users === null) return <Spinner label="Loading users…" />;

  const act = async (fn, message) => {
    try {
      await fn();
      await refresh();
      if (message) toast(message, { tone: 'ok' });
    } catch (err) {
      toast(err.message || 'That did not work', { tone: 'danger' });
    }
  };

  return (
    <div className="page">
      <div className="page-head row-between">
        <div>
          <h1>People</h1>
          <p>Accounts are created here. There is no public sign-up.</p>
        </div>
        <button type="button" className="btn primary" onClick={() => setCreating(true)}>
          <UserPlus size={15} /> Add person
        </button>
      </div>

      <Panel bodyClass="tight">
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Person</th>
                <th>Role</th>
                <th className="num">Assigned</th>
                <th className="num">Done</th>
                <th>Status</th>
                <th>Last sign-in</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td>
                    <strong>{user.display_name}</strong>
                    <div className="tiny faint">{user.username}</div>
                  </td>
                  <td>
                    <select
                      className="select sm"
                      value={user.role}
                      style={{ width: 'auto' }}
                      onChange={(e) =>
                        act(() => admin.updateUser(user.id, { role: e.target.value }), 'Role updated')
                      }
                    >
                      <option value="annotator">annotator</option>
                      <option value="admin">admin</option>
                    </select>
                  </td>
                  <td className="num">{user.assigned_images}</td>
                  <td className="num">{user.completed_images}</td>
                  <td>
                    {!user.is_active && <span className="chip danger">deactivated</span>}
                    {user.is_active && user.must_change_password && (
                      <span className="chip warn">password not set</span>
                    )}
                    {user.is_active && !user.must_change_password && <span className="chip ok">active</span>}
                    {user.locked_until && <span className="chip warn">locked</span>}
                  </td>
                  <td className="tiny faint nowrap">{relativeTime(user.last_login_at) || 'never'}</td>
                  <td className="right">
                    <div className="row gap-1" style={{ justifyContent: 'flex-end' }}>
                      {user.locked_until && (
                        <button
                          type="button"
                          className="btn sm"
                          onClick={() => act(() => admin.updateUser(user.id, { unlock: true }), 'Unlocked')}
                        >
                          Unlock
                        </button>
                      )}
                      {user.must_change_password && (
                        <button
                          type="button"
                          className="btn sm"
                          title="Let them keep the password you gave them instead of setting their own"
                          onClick={() =>
                            act(
                              () =>
                                admin.updateUser(user.id, { must_change_password: false }),
                              'They can keep their current password',
                            )
                          }
                        >
                          Skip password change
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn sm"
                        onClick={async () => {
                          try {
                            const { generated_password } = await admin.resetPassword(user.id);
                            setCredential({ user, password: generated_password, reset: true });
                            refresh();
                          } catch (err) {
                            toast(err.message, { tone: 'danger' });
                          }
                        }}
                      >
                        <KeyRound size={13} /> Reset password
                      </button>
                      {user.is_active ? (
                        <button
                          type="button"
                          className="btn sm danger"
                          onClick={() =>
                            act(() => admin.deactivateUser(user.id), 'Account deactivated')
                          }
                        >
                          Deactivate
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="btn sm"
                          onClick={() =>
                            act(() => admin.updateUser(user.id, { is_active: true }), 'Reactivated')
                          }
                        >
                          Reactivate
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      {creating && (
        <CreateUserDialog
          onClose={() => setCreating(false)}
          onCreated={(user, password) => {
            setCreating(false);
            setCredential({ user, password });
            refresh();
          }}
        />
      )}

      {credential && (
        <PasswordDialog credential={credential} onClose={() => setCredential(null)} />
      )}
    </div>
  );
}

function CreateUserDialog({ onClose, onCreated }) {
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState('annotator');
  const [requireChange, setRequireChange] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { user, generated_password } = await admin.createUser({
        username: username.trim(),
        display_name: displayName.trim() || username.trim(),
        role,
        require_password_change: requireChange,
      });
      onCreated(user, generated_password);
    } catch (err) {
      setError(err.message || 'Could not create the account');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title="Add a person"
      description="A password is generated for them and shown once."
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="create-user" className="btn primary" disabled={busy}>
            Create account
          </button>
        </>
      }
    >
      <form id="create-user" className="stack" onSubmit={submit}>
        <div className="field">
          <label htmlFor="new-username">Username</label>
          <input
            id="new-username"
            className="input"
            value={username}
            required
            autoFocus
            onChange={(e) => setUsername(e.target.value)}
          />
          <span className="hint">Letters, digits, dot, underscore or hyphen.</span>
        </div>
        <div className="field">
          <label htmlFor="new-display">Display name</label>
          <input
            id="new-display"
            className="input"
            value={displayName}
            placeholder={username || 'Their name'}
            onChange={(e) => setDisplayName(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="new-role">Role</label>
          <select id="new-role" className="select" value={role} onChange={(e) => setRole(e.target.value)}>
            <option value="annotator">Annotator — annotates assigned images</option>
            <option value="admin">Admin — everything, including other people's work</option>
          </select>
        </div>
        <div className="field">
          <label className="row gap-1" style={{ cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={requireChange}
              onChange={(e) => setRequireChange(e.target.checked)}
            />
            Make them choose their own password on first sign-in
          </label>
          <span className="hint">
            Recommended. The generated password has to travel to them somehow; requiring a
            change means whatever channel you used stops mattering after they sign in. Turn
            it off only for a small team you trust with the password you hand over.
          </span>
        </div>
        {error && <div className="banner danger">{error}</div>}
      </form>
    </Modal>
  );
}

function PasswordDialog({ credential, onClose }) {
  const [confirmed, setConfirmed] = useState(false);
  const toast = useUiStore((s) => s.toast);

  return (
    <Modal
      title={credential.reset ? 'New password' : 'Account created'}
      onClose={confirmed ? onClose : undefined}
      footer={
        <button type="button" className="btn primary" disabled={!confirmed} onClick={onClose}>
          Done
        </button>
      }
    >
      <p>
        Give this password to <strong>{credential.user.display_name}</strong> yourself — over
        chat or in person. They will be asked to change it when they first sign in.
      </p>

      <div className="banner warn">
        This is the only time it is shown. It is not stored anywhere in readable form.
      </div>

      <div className="row gap-1">
        <code
          className="input mono"
          style={{ fontSize: 'var(--text-lg)', letterSpacing: '0.04em', userSelect: 'all' }}
        >
          {credential.password}
        </code>
        <button
          type="button"
          className="btn"
          onClick={() => {
            navigator.clipboard?.writeText(credential.password);
            toast('Password copied', { tone: 'ok' });
          }}
        >
          <Copy size={14} /> Copy
        </button>
      </div>

      <label className="checkbox">
        <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
        <span>I have saved this password somewhere safe.</span>
      </label>
    </Modal>
  );
}
